import {
  fmt,
  fmtDateShort,
  presetRange,
  type DayRange,
  type PurchaseJournal,
  type RangePreset,
} from '@k2pos/core';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { IconChevronRight } from '@/components/icons';
import { Card } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { cx } from '@/lib/cx';
import { useGoBack } from '@/hooks/useNav';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Purchases.module.css';

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'ມື້ນີ້' },
  { key: 'week', label: '7 ມື້' },
  { key: 'month', label: 'ເດືອນນີ້' },
];

/**
 * Every delivery the shop has taken in.
 *
 * A purchase was visible for exactly one screen — the one right after saving
 * it — and then unreachable forever, even though it had moved stock, moved
 * money and set the cost every future margin is measured against. "What did I
 * pay ບໍລິສັດ X for cement last month" had no answer in the app.
 *
 * One line per delivery, the products behind a tap, the same shape as ອໍເດີ້ and
 * its bill. The lines were listed inline at first and that was wrong: a
 * twenty-product lorry-load pushed the rest of the month off the screen, and a
 * list nobody can scan is not a list.
 */
export function Purchases() {
  const goBack = useGoBack('/more');
  const journal = usePos((s) => s.journal);
  const journalStartedAt = usePos((s) => s.journalStartedAt);
  const [preset, setPreset] = useState<RangePreset>('month');
  const [query, setQuery] = useState('');

  const range: DayRange = useMemo(() => presetRange(preset), [preset]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return journal
      .filter((e): e is PurchaseJournal => e.kind === 'purchase')
      .filter((e) => e.day >= range.from && e.day <= range.to)
      .filter(
        (e) =>
          !q ||
          e.supplier.toLowerCase().includes(q) ||
          e.lines.some((l) => l.name.toLowerCase().includes(q)),
      )
      .reverse(); // newest first
  }, [journal, range, query]);

  const total = rows.reduce((sum, r) => sum + r.total, 0);
  const owed = rows.filter((r) => !r.paid).reduce((sum, r) => sum + r.total, 0);

  return (
    <>
      <AppBar
        title="ປະຫວັດຮັບເຄື່ອງ"
        onBack={goBack}
        subtitle={
          <Text size={11} color={colors.onDarkSoft}>
            {rows.length} ເທື່ອ ·{' '}
            <Mono weight={700} size={13} color={colors.sky}>
              {fmt(total)}
            </Mono>{' '}
            ₭
          </Text>
        }
      />

      <div className={styles.controls}>
        <Field
          value={query}
          onChange={setQuery}
          placeholder="ຄົ້ນຫາຜູ້ຂາຍ ຫຼື ຊື່ສິນຄ້າ..."
          label="ຄົ້ນຫາການຮັບເຄື່ອງ"
        />
        <div className={styles.presets} role="tablist">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={p.key === preset}
              onClick={() => setPreset(p.key)}
              className={cx(styles.chip, p.key === preset && styles.chipOn)}
            >
              <Text
                weight={p.key === preset ? 800 : 500}
                size={11.5}
                color={p.key === preset ? colors.surfaceAlt : colors.textMuted}
              >
                {p.label}
              </Text>
            </button>
          ))}
        </div>

        {/* What is still owed to suppliers, when any of it is. The app does not
            track supplier debt as a balance — ຮັບເຄື່ອງເຂົ້າຮ້ານ says so — so
            this total is the only place the shop can see it at all. */}
        {owed > 0 ? (
          <div className={styles.owed}>
            <Text size={11.5} color={colors.textMuted} className="grow">
              ຄ້າງຈ່າຍຜູ້ຂາຍໃນຊ່ວງນີ້
            </Text>
            <Mono weight={700} size={14} color={colors.skyDeep} className="selectable">
              {fmt(owed)} ₭
            </Mono>
          </div>
        ) : null}
      </div>

      <div className={cx('vscroll', styles.list)}>
        {rows.map((row) => (
          <PurchaseRow key={row.id} row={row} />
        ))}

        {rows.length === 0 ? (
          <div className={styles.empty}>
            <Text size={12.5} color={colors.textMuted} align="center">
              ບໍ່ມີການຮັບເຄື່ອງໃນຊ່ວງນີ້
            </Text>
            <Mono size={10.5} color={colors.textFaint} align="center">
              ຂໍ້ມູນເລີ່ມແຕ່ {journalStartedAt}
            </Mono>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** One delivery, summarised: who it came from, what it cost, how many products. */
function PurchaseRow({ row }: { row: PurchaseJournal }) {
  return (
    <Link to={`/purchases/${row.id}`} className={styles.rowLink}>
      <Card accent={row.paid ? colors.navy : colors.skyDeep} className={styles.row}>
        <div className="grow col">
          <Text weight={700} size={13.5} lines={1} align="left">
            {row.supplier}
          </Text>
          <Mono size={10.5} color={colors.textMuted} lines={1}>
            {fmtDateShort(new Date(row.at))} · {row.lines.length} ລາຍການ
            {row.paid ? ` · ${row.cash ? 'ເງິນສົດ' : 'ໂອນ'}` : ''}
          </Mono>
        </div>

        <div className={styles.rowRight}>
          <Mono weight={700} size={14} className="selectable">
            {fmt(row.total)}
          </Mono>
          <span className={cx(styles.pill, !row.paid && styles.pillOwed)}>
            <Text weight={700} size={9.5} color={row.paid ? colors.textMuted : colors.sky}>
              {row.paid ? 'ຈ່າຍແລ້ວ' : 'ຄ້າງຈ່າຍ'}
            </Text>
          </span>
        </div>

        <IconChevronRight size={16} className={styles.chevron} />
      </Card>
    </Link>
  );
}
